function throttle(func: Function, limit: number) {
  let inThrottle: boolean = false
  return function (this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

export class TrackingManager {
  private static instance: TrackingManager | null = null
  private static refCount: number = 0

  // State
  private durations: Record<string, number> = {}
  private activeCategories: Set<string> = new Set()
  private isIdle: boolean = false
  private isWindowActive: boolean = true
  private lastInteractionTime: number = Date.now()
  private paused: boolean = false // Manual pause

  // Listeners
  private listeners: Set<(state: any) => void> = new Set()

  // Timers & Observers
  private intervalTimer: any = null
  private observer: IntersectionObserver | null = null
  private elementMap: Map<Element, string> = new Map()

  // Interaction (Throttled) - bound to instance
  private handleInteraction = throttle(() => {
    this.lastInteractionTime = Date.now()
    if (this.isIdle) {
      this.isIdle = false
      console.log('用户重新交互，恢复计时')
      this.notify()
    }
  }, 1000)

  public notifyInteraction() {
    console.log('notifyInteraction')
    this.handleInteraction()
  }

  private constructor() {
    this.init()
  }

  public static getInstance(): TrackingManager {
    if (!TrackingManager.instance) {
      TrackingManager.instance = new TrackingManager()
    }
    TrackingManager.refCount++
    return TrackingManager.instance
  }

  public static releaseInstance() {
    if (TrackingManager.refCount > 0) {
      TrackingManager.refCount--
    }
    if (TrackingManager.refCount === 0 && TrackingManager.instance) {
      TrackingManager.instance.dispose()
      TrackingManager.instance = null
    }
  }

  private dispose() {
    this.stopTimers()
    this.stopInteractionListeners()
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    this.activeCategories.clear()
    this.elementMap.clear()
    this.listeners.clear()
    console.log('TrackingManager 已销毁')
  }

  private init() {
    this.isWindowActive = !document.hidden
    this.setupWindowListeners()
    this.setupObserver()
    if (this.isWindowActive) {
      this.startTimers()
    }
  }

  private setupWindowListeners() {
    // Visibility Change
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    // Initial check
    if (!document.hidden) {
      this.startInteractionListeners()
    }
  }

  private startInteractionListeners() {
    window.addEventListener('mousemove', this.handleInteraction)
    window.addEventListener('keydown', this.handleInteraction)
    window.addEventListener('click', this.handleInteraction)
    window.addEventListener('scroll', this.handleInteraction)
  }

  private stopInteractionListeners() {
    window.removeEventListener('mousemove', this.handleInteraction)
    window.removeEventListener('keydown', this.handleInteraction)
    window.removeEventListener('click', this.handleInteraction)
    window.removeEventListener('scroll', this.handleInteraction)
  }

  private setupObserver() {
    // Check if IntersectionObserver is available (SSR or old browsers)
    if (typeof IntersectionObserver === 'undefined') return

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const category = this.elementMap.get(entry.target)
          if (!category) return

          if (entry.isIntersecting) {
            if (!this.activeCategories.has(category)) {
              console.log(`权益分类进入可视区域: ${category}`)
            }
            this.activeCategories.add(category)
          } else {
            if (this.activeCategories.has(category)) {
              console.log(`权益分类移出可视区域: ${category}`)
            }
            this.activeCategories.delete(category)
          }
        })
        this.notify()
      },
      { threshold: 0.1 } // At least 10% visible
    )
  }

  private startTimers() {
    if (this.intervalTimer) return

    // 4s Interval Accumulator
    this.intervalTimer = setInterval(() => {
      this.checkIdle()

      const shouldTrack = !this.isIdle && this.isWindowActive && !this.paused

      if (shouldTrack) {
        let hasUpdates = false
        this.activeCategories.forEach((cat) => {
          this.durations[cat] = (this.durations[cat] || 0) + 4
          hasUpdates = true
        })
        if (hasUpdates) {
          // console.log('计时累加', this.durations) // Optional verbose log
          this.notify()
        }
      }
    }, 4000)
  }

  private stopTimers() {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer)
      this.intervalTimer = null
    }
  }

  private checkIdle() {
    const now = Date.now()
    if (now - this.lastInteractionTime > 30000 && !this.isIdle) {
      this.isIdle = true
      console.log('30秒无交互，停止计时')
      this.notify()
    }
  }

  private handleVisibilityChange = () => {
    this.isWindowActive = !document.hidden
    if (this.isWindowActive) {
      console.log('页面可见，准备恢复计时')
      this.isIdle = false
      this.lastInteractionTime = Date.now()
      this.startInteractionListeners()
      this.startTimers()
    } else {
      console.log('页面不可见，停止计时')
      this.stopInteractionListeners()
      this.stopTimers()
    }
    this.notify()
  }

  private notify() {
    const state = this.getState()
    this.listeners.forEach((cb) => cb(state))
  }

  public getState() {
    return {
      durations: { ...this.durations },
      visibleCategories: Array.from(this.activeCategories),
      isIdle: this.isIdle,
      isWindowActive: this.isWindowActive,
      paused: this.paused
    }
  }

  public subscribe(cb: (state: any) => void) {
    this.listeners.add(cb)
    cb(this.getState()) // Initial emit
    return () => this.listeners.delete(cb)
  }

  public registerRef(category: string, element: HTMLElement | null) {
    if (!element || !this.observer) return
    this.elementMap.set(element, category)
    this.observer.observe(element)
  }

  public unregisterRef(element: HTMLElement | null) {
    if (!element || !this.observer) return
    this.observer.unobserve(element)
    this.elementMap.delete(element)
  }

  public pauseAll() {
    this.paused = true
    console.log('手动暂停所有计时')
    this.notify()
  }

  public resumeAll() {
    this.paused = false
    console.log('手动恢复所有计时')
    this.notify()
  }
}
